<template>
    <div class="home">
        <b-container fluid>
            <b-row>
              <b-col>
                <h1 v-if="!isReadonly">Initial Technician Application Review</h1>
                <h1 v-if="isReadonly">Application Details</h1>
              </b-col>
            </b-row>

            <div class="card">
              <div class="card-header">Application Review</div>
              <div class="card-body">
                  <b-row>
                      <b-col cols="12">
                          <b-button variant="secondary" size="sm" :to="{name: 'appHistory', params: { id: application.applicationId }}" role="button"
                                    v-if="hasHistory()">View Application History</b-button>
                      </b-col>
                  </b-row>
                  <b-row>
                      <b-col cols="12">
                          <p style="font-weight:750;" class="mb-1">Applicant's name</p>
                          {{application.lastName + ', ' + application.firstName + ' ' + application.middleName }}
                      </b-col>
                  </b-row>
                  <b-row>
                      <b-col cols="2">
                        <p style="font-weight:750;" class="mb-1">Application Status</p>
                        {{application.applicationStatus}}
                      </b-col>
                      <b-col cols="2">
                          <p style="font-weight:750;" class="mb-1">Application Status Date</p>
                          {{application.statusDate}}
                      </b-col>
                      <b-col cols="2" v-if="application.applicationStatus === 'WITHDRAW'">
                          <p class="mb-1"><span class="font-weight-bold">Withdraw Date: </span></p>
                              {{application.statusDate}}
                      </b-col>
                      <b-col cols="2">
                        <p style="font-weight:750;" class="mb-1">Payment Status</p>
                        <b-link :to="{ name: 'transactions', params: { id: application.applicationId } }" target="tx">{{application.paymentStatus}}</b-link>
                      </b-col>
                      <b-col cols="2">
                        <p style="font-weight:750;" class="mb-1">I & E Status</p>
                        {{application.screeningStatus}}
                      </b-col>
                      <b-col cols="2">
                          <p style="font-weight:750;" class="mb-1">I&E Referral Date</p>
                          {{hasReferredToIEDate}}
                      </b-col>
                  </b-row>
                  <b-row>
                      <b-col cols="2">
                        <p style="font-weight:750;" class="mb-1">S/S Affirm Status</p>
                        {{application.affirmStatus}}
                      </b-col>
                      <b-col cols="2">
                          <p style="font-weight:750;" class="mb-1">Reopened</p>
                          {{application.edited && application.returnForReview ? "Reopened" : application.edited && !application.returnForReview ? "Resubmitted" : ""}}
                      </b-col>
                      <b-col cols="2">
                          <p style="font-weight:750;" class="mb-1">Reclassify from Temporary</p>
                          {{application.reclassify != null  ? "Yes: #" + application.reclassify : "No"}}
                      </b-col>
                      <b-col cols="2" v-if="(application.license.status === 'ACTIVE' || application.license.status === 'AMENDED')">
                          <b-button size="sm" @click="printCertificate(application.license.licenseId)"
                                    variant="primary"
                                    class="mr-1 mt-1"
                                    >
                              Print Certificate
                          </b-button>
                      </b-col>
                  </b-row>
                  <b-row>
                      <b-col cols="2">
                          <p style="font-weight:750;" class="mb-1">Issued Date</p>
                          {{application.issuedDate}}
                      </b-col>
                      <b-col cols="2">
                          <p style="font-weight:750;" class="mb-1">Expiration Date</p>
                          {{application.expirationDate}}
                      </b-col>

                  </b-row>
                  <b-row class="mt-2">
                      <b-col cols="12">
                          <p style="font-weight:750;" class="mb-1">Notes</p>
                          <b-table hover small show-empty class="sr-caption"
                                   aria-live="polite"
                                   responsive="true"
                                   :fields="noteFields"
                                   :items="application.notes"
                                   :busy="false"
                                   label-sort-asc=""
                                   label-sort-desc=""
                                   caption="Notes table.  To sort the data in this table by a specific column,
                                go to the appropriate column header cell and use enter to sort the data ascending.
                                Use enter again to sort descending.">
                              <template v-slot:no-data>
                                  No Notes Found.
                              </template>
                              <div slot="table-busy" class="text-center text-danger my-2">
                                  <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                                  <strong>Loading...</strong>
                              </div>
                              <template slot="empty" slot-scope="scope">
                                  No Notes Found.
                              </template>
                          </b-table>
                      </b-col>
                  </b-row>
                  <b-row class="mt-2">
                      <b-col cols="12">
                          <p style="font-weight:750;" class="mb-1">Notes Sent to/from Applicant</p>
                          <b-table hover small show-empty class="sr-caption"
                                   aria-live="polite"
                                   responsive="true"
                                   :fields="extNoteFields"
                                   :items="application.externalNotes"
                                   :busy="false"
                                   label-sort-asc=""
                                   label-sort-desc=""
                                   caption="Notes sent to/from applicant table.  To sort the data in this table by a specific column,
                                go to the appropriate column header cell and use enter to sort the data ascending.
                                Use enter again to sort descending.">
                              <template v-slot:no-data>
                                  No Notes Found.
                              </template>
                              <div slot="table-busy" class="text-center text-danger my-2">
                                  <b-spinner class="align-middle" label="Loading, Please Wait" aria-label="Loading, Please Wait"></b-spinner>
                                  <strong>Loading...</strong>
                              </div>
                              <template slot="empty" slot-scope="scope">
                                  No Notes Found.
                              </template>
                          </b-table>
                      </b-col>
                  </b-row>

                  <b-row class="mt-3" v-if="!isReadonly">
                    <b-col cols="6">
                      <BaseParagraphInput v-model="comments" 
                          :v="$v.comments"
                          label="Internal Review Comments"
                          slug="reviewComments"
                          tip=""
                          setId="reviewComments"
                      />
                    </b-col>
                    <b-col cols="6">
                      <BaseParagraphInput v-model="publicComments" 
                          :v="$v.publicComments"
                          label="Public Review Comments"
                          slug="publicComments"
                          tip="These comments WILL BE shared with the applicant."
                          setId="publicComments"
                      />
                    </b-col>
                  </b-row>

                  <b-row class="mt-1" v-if="!isReadonly">
                    <b-col cols="12">
                        <b-dropdown text="Approval Decision" variant="primary" class="m-2" v-if="isActionable">
                          <b-dropdown-item-button @click="showApproveModal">Approve Application</b-dropdown-item-button>
                          <b-dropdown-item-button @click="showDenyModal">Deny Application</b-dropdown-item-button>
                        </b-dropdown>

                        <b-button variant="link" class="mr-4" @click="save">Save</b-button>
                        <b-button variant="link" class="mr-2" @click="downloadPdf">Generate PDF</b-button>
                        <b-button variant="link" class="mr-4" @click="send">Send Message</b-button>
                        <b-button variant="link" class="mr-2" @click="showReopenModal" v-if="application.applicationStatus === 'PENDING'
                            && !application.returnForReview">Reopen Application</b-button>
                        <b-button variant="link" class="mr-2" @click="showWithdrawModal">Withdraw</b-button>
                        <b-dropdown text="Screening Decision (I&E)" variant="link" class="m-2">
                            <b-dropdown-item-button @click="showClearModal" :disabled="application.screeningStatus === 'CLEARED' || application.referredDate !== null">Clear Applicant</b-dropdown-item-button>
                            <b-dropdown-item-button @click="showFlagModal" :disabled="application.screeningStatus === 'FLAGGED' || application.referredDate !== null">Flag Applicant</b-dropdown-item-button>
                            <b-dropdown-item-button @click="showHoldModal" :disabled="application.screeningStatus ==='HOLD' || application.referredDate !== null">Hold Applicant</b-dropdown-item-button>
                            <b-dropdown-item-button @click="showReferModal" :disabled="application.referredDate !== null">Refer to I&E</b-dropdown-item-button>
                        </b-dropdown>
                    </b-col>
                  </b-row>
              </div>
            </div>

            <TechnicianInitial :application="application" :readonly="isReadonly"/>

        </b-container>

        <b-modal id="approve-modal" hide-header hide-footer>
            <h2>Approve Application</h2>
            <hr/>
            <form ref="form" @submit.prevent="approve">
                You are about to approve the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to approve or Cancel to return to the application review.</p>
                <div>
                    <b-button @click="closeApproveModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="deny-modal" hide-header hide-footer>
            <h2>Deny Application</h2>
            <hr/>
            <form ref="form" @submit.prevent="deny">
                You are about to deny the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to deny or Cancel to return to the application review.</p>
                <div>
                    <b-button @click="closeDenyModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="withdraw-modal" hide-header hide-footer>
            <h2>Withdraw Application</h2>
            <hr/>
            <form ref="form" @submit.prevent="withdraw">
                You are about to withdraw the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to withdraw or Cancel to return to the application review.</p>
                <div>
                    <b-button @click="closeWithdrawModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="reopen-modal" hide-header hide-footer>
            <h2>Reopen Application</h2>
            <hr/>
            <form ref="form" @submit.prevent="reopen">
                You are about to reopen the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to reopen or Cancel to return to the application review.</p>
                <div>
                    <b-button @click="closeReopenModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="refer-modal" hide-header hide-footer>
            <h2>Refer application to I&E</h2>
            <hr/>
            <form ref="form" @submit.prevent="refer">
                You are about to refer the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to refer this application to I&E or Cancel to return to the application review.</p>
                <div>
                    <b-button @click="closeReferModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="clear-modal" hide-header hide-footer>
            <h2>Clear Application</h2>
            <hr/>
            <form ref="form" @submit.prevent="clear">
                You are about to clear the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to clear the I&E screening or Cancel to return to the application review.</p>
                <div>
                    <b-button @click="closeClearModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="flag-modal" hide-header hide-footer>
            <h2>Flag application</h2>
            <hr/>
            <form ref="form" @submit.prevent="flag">
                You are about to flag the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to flag this application or Cancel to return to the application review.</p>
                <div>
                    <b-button @click="closeFlagModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
        <b-modal id="hold-modal" hide-header hide-footer>
            <h2>Hold application</h2>
            <hr/>
            <form ref="form" @submit.prevent="hold">
                You are about to hold the {{application.license.licenseType.description}} application from user {{application.firstName}} {{application.lastName}}.
                <p class="mt-2">Press Confirm to hold this application to I&E or Cancel to return to the application review.</p>
                <div>
                    <b-button @click="closeHoldModal" style="margin-right:15px;" variant="secondary">Cancel</b-button>
                    <b-button type="submit" variant="primary">Confirm</b-button>
                </div>
            </form>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios'
import { required, maxLength } from 'vuelidate/lib/validators'
import TechnicianInitial from '@/components/templates/TechnicianInitial.vue'
import * as FileSaver from 'file-saver'
import moment from 'moment'

export default {
  components: {
    TechnicianInitial
  },

  data: () => ({
    application: {},
    comments: null,
    publicComments: null,
    noteFields: [
        {key: 'note', sortable: true, label: 'Note'},
        {key: 'noteType.description', sortable: true, label: 'Note Type'},
        {key: 'noteDate', sortable: true, label: 'Note Date'},
        {key: 'noteByName', sortable: true, label: 'Note By'}
    ],
    extNoteFields: [
        {key: 'note', sortable: true, label: 'Note'},
        {key: 'noteType.description', sortable: true, label: 'Note Type'},
        {key: 'noteDate', sortable: true, label: 'Note Date'},
        {key: 'noteByName', sortable: true, label: 'Note By'}
    ],
    isReadonly: true,
  }),
  
  mounted() {
    this.loadApplication()
  },

  computed: {
    isActionable: function() {
      var statuses = ["PENDING","AMENDED","PENDING SUPERVISOR"]
      var screenStatuses = ["PENDING","AMENDED","PENDING SUPERVISOR","HOLD"]
      return statuses.some(s => s === this.application.applicationStatus) &&
             this.application.paymentStatus === 'CONFIRMED' &&
             !screenStatuses.some(s => s === this.application.screeningStatus)
    },
    hasReferredToIEDate: function(){
        return this.application.referredDate === null ? "N/A" : this.application.referredDate;
    }
  },

  validations: {
    comments: { required: false, maxLength: maxLength(4000) },
    publicComments: { required: false, maxLength: maxLength(4000) }
  },

  methods: {
    loadApplication() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/${this.$route.params.id}`)
        .then(response => {
          var statuses = ["PENDING","AMENDED","PENDING SUPERVISOR"]
          this.application = response.data
          this.isReadonly = (this.$route.name === "readonlyInitial" || !statuses.some(s => s === this.application.applicationStatus))
        })
    },

    showApproveModal() {
        this.$bvModal.show('approve-modal')
    },

    closeApproveModal() {
        this.$bvModal.hide('approve-modal')
    },

    showDenyModal() {
        this.$bvModal.show('deny-modal')
    },

    closeDenyModal() {
        this.$bvModal.hide('deny-modal')
    },

    showWithdrawModal() {
        this.$bvModal.show('withdraw-modal')
    },

    closeWithdrawModal() {
        this.$bvModal.hide('withdraw-modal')
    },

    showReopenModal() {
        this.$bvModal.show('reopen-modal')
    },
      closeReopenModal() {
          this.$bvModal.hide('reopen-modal')
      },

    showReferModal(){
        this.$bvModal.show('refer-modal')
    },
      closeReferModal() {
          this.$bvModal.hide('refer-modal')
      },
      showClearModal(){
        this.$bvModal.show('clear-modal')
      },
      closeClearModal(){
        this.$bvModal.hide('clear-modal')
      },
      showHoldModal(){
          this.$bvModal.show('hold-modal')
      },
      closeHoldModal(){
          this.$bvModal.hide('hold-modal')
      },
      showFlagModal(){
        this.$bvModal.show('flag-modal')
      },
      closeFlagModal(){
          this.$bvModal.hide('flag-modal')
      },

    approve() {
      this.submitToServer('approve')
    },

    deny() {
      this.submitToServer('deny')
    },

    withdraw() {
      this.submitToServer('withdraw')
    },

    save() {
      this.submitToServer('save')
    },
      refer() {
        this.submitToServer('refer')
      },
      clear(){
        this.submitToServer('clear')
      },
      hold(){
        this.submitToServer('hold')
      },
      flag(){
        this.submitToServer('flag')
      },

    submitToServer(action) {
      const payload = {
        applicationId: this.$route.params.id,
        applicationNotes: this.comments
      }

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/approval/${action}`, payload)
        .then(response => { 
          if (action != 'save') {
            location.reload()
           this.showRootToast("Application Updates", "Your licensing decision has been updated.", "success")
          } else {
            this.showRootToast("Application Updates", "The comments have been updated.", "success")
            if (!this.application.notes) {
                this.application.notes = []
            }
            this.application.notes.push({
                noteType: {
                    description: "Credentialing (MDH)"
                },
                note: this.comments,
                noteDate: moment().format('YYYY-MM-DD'),
                noteByName: "Me"
            })
            this.comments = null
          }
      }).catch(error => {
          if (error.response.status === 406) {
              this.showRootToast("Application Not Withdrawn",
                  "Only applications in Pending status can be withdrawn.", "danger")
          } else {
              this.showRootToast("Error", "An error has occurred while processing your request", "danger")
          }
      })
    },

    showRootToast(title, message, type) {
      this.$root.$bvToast.toast(message, {
            title: title,
            toaster: 'b-toaster-top-center',
            solid: true,
            variant: type,
           appendToast: true,
            noAutoHide:true
          })
        this.toastFocus()
    },
    toastFocus() {
        setTimeout(() => {
            let elem = document.getElementsByClassName("toast")
            elem = elem[elem.length-1]
            elem.focus()
        }, 100)
    },

    downloadPdf() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/pdf/application/${this.$route.params.id}`)
          .then(response => {
              const file = response.data;
              if (file.contentType == null) {
                  return;
              }

              const byteString = atob(file.fileData);
              const arrayBuffer = new ArrayBuffer(byteString.length);
              const int8Array = new Uint8Array(arrayBuffer);
              for (let i = 0; i < byteString.length; i++) {
                  int8Array[i] = byteString.charCodeAt(i);
              }
              const blob = new Blob([int8Array], {type: file.contentType});
              FileSaver.saveAs(blob, file.name);
          });
    },

    reopen() {
      const payload = {
        applicationId: this.$route.params.id,
        applicationNotes: this.comments,
        publicNotes: this.publicComments
      }

      axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/reopen`, payload)
          .then(response => {
              this.showRootToast("Application Reopen", "The application has been reopened.", "success")
              this.application.returnForReview = true
              if (!this.application.externalNotes) {
                  this.application.externalNotes = []
              }
              this.application.externalNotes.push({
                  noteType: {
                      description: "Credentialing (MDH)"
                  },
                  note: this.publicComments,
                  noteDate: moment().format('YYYY-MM-DD'),
                  noteByName: "Me"
              })
              this.publicComments = null
              this.closeReopenModal()
          }).catch(error => {
            if (error.response.status === 406) {
                this.showRootToast("Application Not Reopened",
                    "Only applications in Pending status can be reopened.", "danger")
            } else {
                this.showRootToast("Error", "An error has occurred while processing your request", "danger")
            }
      })
    },

    send() {
        const payload = {
            applicationId: this.$route.params.id,
            publicNotes: this.publicComments
        }

        axios.post(`${process.env.VUE_APP_API_BASE_URL}/application/extMsg/true`, payload)
            .then(response => {
                this.showRootToast("Application Message", "The application has been updated.", "success")
                if (!this.application.externalNotes) {
                    this.application.externalNotes = []
                }
                this.application.externalNotes.push({
                    noteType: {
                        description: "Credentialing (MDH)"
                    },
                    note: this.publicComments,
                    noteDate: moment().format('YYYY-MM-DD'),
                    noteByName: "Me"
                })
                this.publicComments = null
            });
    },

    hasHistory() {
        return (this.application.prevAddress != null && this.application.prevAddress.length > 0)
            || (this.application.prevContacts != null && this.application.prevContacts.length > 0)
            || (this.application.prevMnSupervisor != null && this.application.prevMnSupervisor.length > 0)
            || (this.application.prevQuestionResponses != null && this.application.prevQuestionResponses.length > 0)
            || (this.application.prevAttachments != null && this.application.prevAttachments.length > 0)
    },

    printCertificate(id) {
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/certificate/generate/${id}`)
            .then(response => {
                const file = response.data;
                if (file.contentType == null) {
                    return;
                }

                const byteString = atob(file.fileData);
                const arrayBuffer = new ArrayBuffer(byteString.length);
                const int8Array = new Uint8Array(arrayBuffer);
                for (let i = 0; i < byteString.length; i++) {
                    int8Array[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([int8Array], {type: file.contentType});
                FileSaver.saveAs(blob, file.name);
            });
    },
  },

  created() {
    document.title = "Initial Technician Application Review - Body Art"
  }
}
</script>

<style>
</style>